/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */

import { Injectable, Inject } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { SoAuthenticator } from "../utilities/soAuthenticator";
import { ConfigurationService } from "./configuration.service";
import { ILoggedIn } from "../model/user.model";
import { UserInfoService } from "./userinfo.service";
import { MonitoringService } from "./monitoring.service";
import { LoginResponse } from "../model/loginResponse.model";
import { OutlookService } from "./outlook/outlook.service";
import { SharedService } from "./shared.service";
import { AccountInfo, AuthenticationResult, IdTokenClaims, IPublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { LoadedContextService } from "./loadedContext.service";
import { firstValueFrom } from "rxjs";
import { AuthSharedService } from "./auth.service.shared";
import { MsalService } from "./msal.service";
import { UIErrorFactory } from "./ui-error.factory";
import { UiErrorService } from "./ui-error-service/ui-error.service";

type TokenType = "accessToken" | "idToken";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private static readonly TOKEN_RENEWAL_OFFSET_MS = 5 * 60_000;
  private soleAccountMismatchLogged = false;
  msalCacheKey: string;
  scopes: string[] = ["email", "openid", "profile", "User.Read", "offline_access"];

  constructor(
    private readonly router: Router,
    private readonly httpClient: HttpClient,
    private readonly configService: ConfigurationService,
    private readonly userInfo: UserInfoService,
    private readonly monitoringService: MonitoringService,
    @Inject("BASE_URL") private readonly baseUrl: string,
    private readonly outlookService: OutlookService,
    private readonly soAuth: SoAuthenticator,
    private readonly sharedService: SharedService,
    private readonly loadedContextService: LoadedContextService,
    private readonly authSharedService: AuthSharedService,
    private readonly msalService: MsalService,
    private readonly uiErrorService: UiErrorService,
    private readonly uiErrorFactory: UIErrorFactory
  ) {
    this.msalCacheKey = `|${this.configService.config.azureClientId}|SuperOfficeForOutlook`;
    if (this.loadedContextService.isCalendar()) {
      this.scopes.push("Calendars.ReadWrite");
    } else {
      this.scopes.push("Mail.ReadWrite");
    }
  }

  ensureSharedMailboxScope(): void {
    if (!this.scopes.includes("Mail.ReadWrite.Shared")) {
      this.scopes.push("Mail.ReadWrite.Shared");
      // A stored token was issued for the old scope set; Graph answers 403 to it, which no retry path recovers from.
      this.clearAccessTokenCache();
    }
  }

  public async isEnrolled(): Promise<ILoggedIn> {
    const result = await firstValueFrom(this.httpClient.post<ILoggedIn>(`${this.baseUrl}api/auth/isEnrolled`, {}));
    this.userInfo.setUserInfo(result);
    // keeps front-end telemetry tagged with the same authenticated-user context as the backend
    this.monitoringService.updateUserInfo();

    return result;
  }

  async completeOnsiteAuthentication(url: string, username: string, password: string): Promise<number> {
    if (!this.hasClientAppSession()) {
      await this.login();
    }

    try {
      await firstValueFrom(
        this.httpClient.post<ILoggedIn>(`${this.baseUrl}api/auth/onsite/signin`, {
          url,
          username,
          password,
        })
      );

      return 1;
    } catch (error: any) {
      // Display authentication popup
      if (error?.status === 401) {
        return 401;
      }
      // Display generic popup
      return -1;
    }
  }

  async startAuthentication(): Promise<void> {
    await this.soAuth.authenticate();

    const returnUrl = this.getReturlUrl();
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    }
  }

  private getReturlUrl(): string {
    try {
      return localStorage.getItem("returnUrl");
    } catch (e) {
      console.log("Error (getReturlUrl) : " + JSON.stringify(e));
      return "/";
    }
  }

  async ensureHasClientAppSession(): Promise<void> {
    if (!this.hasClientAppSession()) {
      await this.login();
    }
  }

  hasClientAppSession(): boolean {
    if (!this.userInfo.clientAppBearer) {
      return false;
    }
    if (!this.userInfo.clientAppExpiration) {
      return false;
    }

    const now = Date.now();
    const utcExpiration = 1000 * this.userInfo.clientAppExpiration;
    return now < utcExpiration;
  }

  async login(): Promise<void> {
    try {
      const token = await this.getIdToken();
      const result = await firstValueFrom(this.httpClient.post<LoginResponse>(`${this.baseUrl}api/auth/login`, { token }));
      this.userInfo.setClientAppInfo(result);
    } catch (error) {

      const customError = this.uiErrorFactory.createSOffice365APIAuthInterrupted(
        this.monitoringService.getSessionId(), error);

      this.monitoringService.logFailed("FAILED: login", customError);

      throw customError;
    }
  }

  async getIdToken(): Promise<string> {
    return this.isDesktop() ? this.getTokenFromDesktop("idToken") : this.getTokenFromWeb("idToken");
  }

  async getAccessToken(interactionRequired: boolean = false, additionalChallenges?: string, allowInteractive: boolean = true): Promise<string> {
    this.monitoringService.logCustomEvent("INFO: get accessToken", { interactionRequired });

    if (interactionRequired) {
      // additionalChallenges isn't forwarded: this is a full interactive sign-in, and AAD enforces
      // any required step-up live during it — forwarding was tried and had no observable effect.
      const result = await this.getInteractiveResultViaDialog("accessToken");
      this.clearMsalCache();
      await this.setActiveAccountAndCache(result);
      return result.accessToken;
    }

    return this.isDesktop()
      ? await this.getTokenFromDesktop("accessToken", additionalChallenges, allowInteractive)
      : await this.getAccessTokenFromWeb(additionalChallenges, allowInteractive);
  }

  private isDesktop(): boolean {
    return this.sharedService.isRunningOnDesktop();
  }

  private async getAccessTokenFromWeb(additionalChallenges?: string, allowInteractive: boolean = true): Promise<string> {
    return this.getTokenThroughSessionCache("accessToken", false, additionalChallenges,
      () => this.acquireAccessTokenFromWeb(additionalChallenges, allowInteractive));
  }

  private async acquireAccessTokenFromWeb(additionalChallenges?: string, allowInteractive: boolean = true): Promise<AuthenticationResult> {
    const msal = await this.getMsalInstance();
    const account = this.resolveAccount(msal);
    const startedAt = performance.now();
    try {
      const result = await msal.acquireTokenSilent({ scopes: this.scopes, claims: additionalChallenges, account });
      this.logSilentAcquisition("accessToken", false, result, additionalChallenges, startedAt);
      return result;
    } catch (silentError) {
      this.monitoringService.logCustomEvent(`FAILED: accessToken silent acquisition`, { IsOutlookOnDesktop: false });
      if (!allowInteractive) throw silentError;
      const isIncremental = msal.getAllAccounts().length > 0 && await this.isIncrementalConsentError(silentError, msal, account);
      return await this.acquireTokenPopupWithHandling("accessToken", additionalChallenges, isIncremental, account);
    }
  }

  private async getTokenFromWeb(type: TokenType, additionalChallenges?: string): Promise<string> {
    const msal = await this.getMsalInstance();
    const account = this.resolveAccount(msal);
    const startedAt = performance.now();
    try {
      const result = await msal.acquireTokenSilent({ scopes: this.scopes, claims: additionalChallenges, account });
      this.logSilentAcquisition(type, false, result, additionalChallenges, startedAt);
      return result[type];
    } catch (silentError) {
      this.monitoringService.logCustomEvent(`FAILED: ${type} silent acquisition`, { IsOutlookOnDesktop: false });
      const isIncremental = msal.getAllAccounts().length > 0 && await this.isIncrementalConsentError(silentError, msal, account);
      const result =
        type === "idToken"
          ? await this.loginPopupWithHandling(type, additionalChallenges, isIncremental, account)
          : await this.acquireTokenPopupWithHandling(type, additionalChallenges, isIncremental, account);
      return result[type];
    }
  }

  private async getTokenFromDesktop(type: TokenType, additionalChallenges?: string, allowInteractive: boolean = true): Promise<string> {
    return this.getTokenThroughSessionCache(type, true, additionalChallenges,
      () => this.acquireTokenFromDesktop(type, additionalChallenges, allowInteractive));
  }

  // A claims challenge means the cached token was just rejected. The replacement is stored because MSAL keeps
  // claims-acquired tokens under a separate cache key and would keep handing the rejected one back to no-claims calls.
  private async getTokenThroughSessionCache(
    type: TokenType, isDesktop: boolean, additionalChallenges: string | undefined, acquire: () => Promise<AuthenticationResult>
  ): Promise<string> {
    if (additionalChallenges) {
      sessionStorage.removeItem(this.getCacheKey(type));
      const result = await acquire();
      this.storeTokenInCache(result);
      return result[type];
    }

    const cached = this.getCachedToken(type);
    if (cached) {
      this.monitoringService.logCustomEvent(`INFO: getting ${type} from cache`, { IsOutlookOnDesktop: isDesktop });
      return cached;
    }

    return (await acquire())[type];
  }

  // Broker cache is shared with other Office apps/tenants on the machine and can hold multiple accounts.
  private resolveAccount(msal: IPublicClientApplication): AccountInfo | undefined {
    const accounts = msal.getAllAccounts();
    if (!accounts.length) return undefined;

    const email = this.outlookService.userProfile.emailAddress;
    // getAccount({username}) matches the UPN, which isn't always the mailbox's SMTP address;
    // loginHint also matches the login_hint/upn ID-token claims, covering that mismatch.
    const matchingAccount = msal.getAccount({ username: email }) ?? msal.getAccount({ loginHint: email }) ?? undefined;
    if (matchingAccount) {
      msal.setActiveAccount(matchingAccount);
      return matchingAccount;
    }

    if (accounts.length > 1) {
      // Several cached accounts and none belongs to the signed-in mailbox: picking one is a guess,
      // and guessing wrong is exactly the defect this change fixes. Fall through to interactive
      // auth instead, which is issued with loginHint = the signed-in mailbox.
      this.monitoringService.logCustomEvent(
        "WARN: no cached MSAL account matches signed-in mailbox among several candidates, deferring to interactive sign-in",
        { cachedAccountDomains: accounts.map(a => a.username?.split("@")[1] ?? "unknown").join(", ") }
      );
      return undefined;
    }

    if (!this.soleAccountMismatchLogged) {
      this.soleAccountMismatchLogged = true;
      this.monitoringService.logCustomEvent("WARN: sole cached MSAL account doesn't match signed-in mailbox, using it anyway", {
        cachedAccountDomain: accounts[0].username?.split("@")[1] ?? "unknown",
      });
    }
    msal.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  private async acquireTokenFromDesktop(
    type: TokenType, additionalChallenges?: string, allowInteractive: boolean = true
  ): Promise<AuthenticationResult> {
    const msal = await this.getMsalInstance();
    const account = this.resolveAccount(msal);
    const startedAt = performance.now();
    try {
      const result = await msal.acquireTokenSilent({ scopes: this.scopes, claims: additionalChallenges, account });
      msal.setActiveAccount(result.account);
      this.logSilentAcquisition(type, true, result, additionalChallenges, startedAt);
      return result;
    } catch (silentError) {
      this.monitoringService.logCustomEvent(`FAILED: ${type} silent acquisition`, { silentError });
      if (!allowInteractive) throw silentError;
      const result = await this.getInteractiveResultViaDialog(type);
      await this.setActiveAccountAndCache(result);
      return result;
    }
  }

  // fromCache=false means the request reached the host broker (NAA) - the round trip users may see as a host "connecting" window.
  private logSilentAcquisition(
    type: TokenType, isDesktop: boolean, result: AuthenticationResult, additionalChallenges: string | undefined, startedAt: number
  ): void {
    this.monitoringService.logCustomEvent(`INFO: acquired ${type} silently`, {
      IsOutlookOnDesktop: isDesktop,
      fromCache: result.fromCache,
      withClaims: !!additionalChallenges,
      durationMs: Math.round(performance.now() - startedAt),
    });
  }

  private async getInteractiveResultViaDialog(type: TokenType): Promise<AuthenticationResult> {
    try {
      const result = await this.authenticateWithDialog();
      this.monitoringService.logCustomEvent(`INFO: acquired ${type} interactively via dialog`);
      return result;
    } catch (error) {
      this.monitoringService.logCustomEvent(`FAILED: ${type} acquisition interactively via dialog`, { error });
      throw error;
    }
  }

  private async loginPopupWithHandling(
    type: TokenType, additionalChallenges?: string, isIncremental?: boolean, account?: AccountInfo
  ): Promise<AuthenticationResult> {
    const msal = await this.getMsalInstance();
    try {
      const result = await msal.loginPopup({
        scopes: this.scopes,
        claims: additionalChallenges,
        account,
        loginHint: account ? undefined : this.outlookService.userProfile.emailAddress,
      });
      this.monitoringService.logCustomEvent(`INFO: acquired ${type} via popup`, { IsOutlookOnDesktop: false });
      return result;
    } catch (popupError) {
      this.monitoringService.logCustomEvent(`FAILED: ${type} acquisition via popup for Outlook Web`, { popupError });
      await this.handlePopupError(popupError, isIncremental ?? false);
      throw popupError;
    }
  }

  private async acquireTokenPopupWithHandling(
    type: TokenType, additionalChallenges?: string, isIncremental?: boolean, account?: AccountInfo
  ): Promise<AuthenticationResult> {
    const msal = await this.getMsalInstance();
    try {
      const result = await msal.acquireTokenPopup({
        scopes: this.scopes,
        claims: additionalChallenges,
        account,
        loginHint: account ? undefined : this.outlookService.userProfile.emailAddress,
      });
      this.monitoringService.logCustomEvent(`INFO: acquired ${type} via popup`, { IsOutlookOnDesktop: false });
      return result;
    } catch (popupError) {
      this.monitoringService.logCustomEvent(`FAILED: ${type} acquisition via popup for Outlook Web`, { popupError });
      await this.handlePopupError(popupError, isIncremental ?? false);
      throw popupError;
    }
  }

  private async setActiveAccountAndCache(result: AuthenticationResult): Promise<void> {
    const msal = await this.getMsalInstance();
    msal.setActiveAccount(result.account);
    this.storeTokenInCache(result);
  }

  private async handlePopupError(error: any, isIncremental: boolean): Promise<void> {
    const errorCode = error?.errorCode;

    switch (errorCode) {
      case "popup_window_error":
        console.warn("Popup blocked.");
        this.router.navigateByUrl("/popup-blocked");
        break;
      case "interaction_in_progress":
        console.warn("Interaction already in progress.");
        this.router.navigateByUrl("/authorization-in-progress");
        break;
      default:
        console.warn("Unhandled popup error", errorCode);
        this.router.navigate(["/authorization-info"], {
          state: { isNewUser: !isIncremental, requestedScopes: this.scopes }
        });
    }
  }

  private async isIncrementalConsentError(error: unknown, msal: IPublicClientApplication, account?: AccountInfo): Promise<boolean> {
    if (!(error instanceof InteractionRequiredAuthError)) return false;

    const isKnownIncrementalPattern =
      error.errorCode === "consent_required"
      || error.errorCode === "interaction_required"
      || error.subError === "consent_required"
      || (error.errorCode === "invalid_grant" && error.errorMessage?.includes("AADSTS65001"));

    if (!isKnownIncrementalPattern) {
      this.monitoringService.logCustomEvent("UnknownInteractionRequiredAuthError", {
        errorCode: error.errorCode,
        subError: error.subError,
        errorMessage: error.errorMessage,
      });
      return false;
    }

    // Distinguish true incremental consent (new scopes added to existing session) from
    // a full re-consent scenario (e.g. enterprise app deleted). If even base scopes fail
    // silently, the existing consent is entirely gone → treat as new user, not incremental.
    try {
      await msal.acquireTokenSilent({ scopes: ["openid", "profile"], account });
      return true;
    } catch {
      return false;
    }
  }

  private async getMsalInstance(): Promise<IPublicClientApplication> {
    return await this.msalService.getInstance();
  }

  // Holds only tokens SOFO must hand out itself: interactive results and claims-challenge replacements.
  private getCachedToken(type: TokenType): string | null {
    const cachedToken = sessionStorage.getItem(this.getCacheKey(type));

    if (cachedToken) {
      const tokenData = JSON.parse(cachedToken);
      if (Date.now() + AuthService.TOKEN_RENEWAL_OFFSET_MS < parseInt(tokenData.expiresOn)) {
        return tokenData[type] ?? null;
      } else {
        this.monitoringService.logCustomEvent(`INFO: Removing expired ${type} from cache`);
        sessionStorage.removeItem(this.getCacheKey(type));
      }
    }

    return null;
  }

  private getCacheKey(prefix: TokenType): string {
    return `${prefix}|${this.configService.config.azureClientId}|SuperOfficeForOutlook`;
  }

  private authenticateWithDialog(): Promise<AuthenticationResult> {
    this.monitoringService.logCustomEvent("INFO: Authenticating with dialog");
    return new Promise((resolve, reject) => {
      this.authSharedService.setAuthInProgress(true);

      const clientIdParameter = `clientId=${encodeURIComponent(this.configService.config.azureClientId)}`;
      const loginHint = `&loginHint=${encodeURIComponent(this.outlookService.userProfile.emailAddress)}`;
      const scopesParameter = `&scopes=${encodeURIComponent(this.scopes.join(" "))}`;

      const authPageUrl = `${window.location.origin}/assets/auth-dialog.html?${clientIdParameter}${loginHint}${scopesParameter}`;
      this.outlookService.openDialog(
        authPageUrl,
        40,
        60,
        (message) => {
          const authenticationResult = JSON.parse(message) as AuthenticationResult;
          this.authSharedService.setAuthInProgress(false);
          this.monitoringService.logCustomEvent("INFO: Successfully authenticated with dialog");
          resolve(authenticationResult);
        },
        (error) => {
          this.monitoringService.logCustomEvent("FAILED: authenticating with dialog", { error });
          reject(error);
          this.authSharedService.setAuthInProgress(false);
        },
        (logMessage: string, error: any | null) => this.monitoringService.logCustomEvent(logMessage, error ? { error } : undefined)
      );
    });
  }

  private storeTokenInCache(authenticationResult: AuthenticationResult) {
    const { accessToken, expiresOn, idToken, idTokenClaims } = authenticationResult;

    sessionStorage.setItem(
      this.getCacheKey("accessToken"),
      JSON.stringify({
        accessToken: accessToken,
        expiresOn: new Date(expiresOn).getTime().toString(),
      })
    );

    // expiresOn is the access token's expiry, which can outlive the id token by hours (CAE-capable tokens).
    const idTokenExpSeconds = (idTokenClaims as IdTokenClaims | undefined)?.exp;
    if (!idToken || !idTokenExpSeconds) {
      sessionStorage.removeItem(this.getCacheKey("idToken"));
      return;
    }

    sessionStorage.setItem(
      this.getCacheKey("idToken"),
      JSON.stringify({
        idToken: idToken,
        expiresOn: (idTokenExpSeconds * 1000).toString(),
      })
    );
  }

  clearAccessTokenCache(): void {
    sessionStorage.removeItem(this.getCacheKey("accessToken"));
  }

  private clearTokensFromCache(): void {
    sessionStorage.removeItem(this.getCacheKey("accessToken"));
    sessionStorage.removeItem(this.getCacheKey("idToken"));
  }

  private async clearMsalCache(): Promise<void> {
    this.monitoringService.logCustomEvent("INFO: clearing sessionStorage msal");
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith("msal.")) {
          sessionStorage.removeItem(key);
        }
      });

      this.monitoringService.logCustomEvent("INFO: successfully cleared sessionStorage for msal");
    } catch (error) {
      this.monitoringService.logCustomEvent("FAILED: to clear sessionStorage for msal", { error });
    }
  }

  async signout(): Promise<void> {
    await firstValueFrom(this.httpClient.post(`${this.baseUrl}api/auth/signout`, {}), { defaultValue: null });
    this.userInfo.removeClientAppInfo();
    this.clearTokensFromCache();
    this.clearMsalCache();
  }
}
